import {
  Alert,
  Avatar,
  Button,
  Col,
  Comment,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ExportRecord, Field, Grid, GridRow, Integration, IntegrationExecution, User } from '../../models';
import { apiClient, apiErrorMessage, getFieldIdsUserCanEditValue, hasBumpRevisionPermission, hasCopyRecordPermission } from '../../utils';
import { UserContext } from '../contexts';
import { RecordRevisionsTable } from './record-revisions-table';
import { RowCardPage } from './row-card-page';
import { BugOutlined, CommentOutlined, CopyOutlined, ExclamationCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import { RecordRevisionsHistoryTable } from './record-revisions-history-table';
import { MiniSDSSettingsForm } from './minisds-settings-form';

export function RowDetailsPage(props: {
  grid?: Grid;
  row?: GridRow;
  onRowAdded: () => void;
  onSaved: (row: GridRow) => void;
  onDelete: () => void;
  onSubGridChanged?: () => void;
}) {
  const { TabPane } = Tabs;
  const { Text } = Typography;
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState<GridRow>();
  const [integrations, setIntegrations] = useState<Integration[]>();
  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [summaryFields, setSummaryFields] = useState<Field[]>();

  const { user } = useContext(UserContext);

  useEffect(() => {
    const editableFieldIds = getFieldIdsUserCanEditValue(user, props.grid?.id);
    setHasEditPermission(!!editableFieldIds && editableFieldIds.length > 0);
  }, [props.grid?.id, props.row, user]);

  const loadIntegrations = useCallback(() => {
    apiClient
      .get(`integrations?type=EXPORT`)
      .then(response => {
        if (response.data) {
          let responseData = response.data as Integration[];
          responseData = responseData?.filter(i => i.primaryGridId === props.grid?.id);
          setIntegrations(responseData);
        } else {
          setIntegrations(undefined);
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.grid?.id]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    if (props.row) {
      setRow(props.row);
      const fields = props.grid?.fields?.filter(f => f.isIdentifier && !f.isRevision);
      const revisionField = props.grid?.fields?.find(f => f.isRevision);
      if (!!revisionField) {
        fields?.push(revisionField);
      }
      setSummaryFields(fields);
    }
  }, [props.row, props.grid]);

  const loadRow = () => {
    const queryParams = [`gridId=${props.grid?.id}`, `rowId=${props.row?.id}`];
    const queryParamsStr = queryParams.join('&');

    setLoading(true);
    apiClient
      .get(`records/record?${queryParamsStr}`)
      .then((response: any) => {
        setRow(response.data as GridRow);
        props.onSaved(response.data as GridRow);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {(summaryFields?.length || 0) > 0 && (
        <Space split="," style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
          {summaryFields?.map(f => (
            <Space>
              <Text strong>{f.name}:</Text>
              <Text>{row?.cells?.[f.id]?.value}</Text>
            </Space>
          ))}
        </Space>
      )}
      <Spin spinning={loading}>
        <Tabs>
          <TabPane tab="Kart" key="2">
            <RowCardPage rowId={props.row?.id} grid={props.grid} onSaved={props.onSaved} onSubGridChanged={props.onSubGridChanged} />
          </TabPane>
          <TabPane tab="Genel" key="1">
            <Details grid={props.grid} row={row} onSaved={props.onSaved} onDelete={props.onDelete} />
          </TabPane>
          {row && props.grid?.isRevision && (
            <>
              <TabPane tab="Revizyon geçmişi" key="5">
                <RecordRevisionsHistoryTable grid={props.grid} gridRow={row} onRowAdded={props.onRowAdded} />
              </TabPane>
              <TabPane tab="Tüm revizyonlar" key="6">
                <RecordRevisionsTable grid={props.grid} gridRow={row} onRowAdded={props.onRowAdded} />
              </TabPane>
            </>
          )}
          {row && !row.isObsolete && (
            <TabPane tab="İşlemler" key="3">
              <Actions grid={props.grid} row={row} onRowAdded={props.onRowAdded} onDelete={props.onDelete} onRowSaved={loadRow} />
            </TabPane>
          )}
          {hasEditPermission && !row?.isObsolete && !props.grid?.parentFieldId && (
            <TabPane tab="Aktarımlar" key="4">
              <Integrations grid={props.grid} gridRow={row} integrations={integrations} />
            </TabPane>
          )}
          <TabPane tab={props.row?.recordCommentCount ? 'Yorumlar (' + props.row?.recordCommentCount + ')' : 'Yorumlar'} key="7">
            <RecordComments gridId={props.grid?.id} recordId={row?.id} />
          </TabPane>
        </Tabs>
      </Spin>
    </>
  );
}

function Details(props: { grid?: Grid; row?: GridRow; onSaved: (row: GridRow) => void; onDelete: () => void }) {
  const { Text } = Typography;

  const [row, setRow] = useState(props.row);

  useEffect(() => {
    setRow(props.row);
  }, [props.row]);

  return (
    <>
      {row?.isObsolete && (
        <Alert
          type="error"
          message={
            <Space direction="vertical" size={0}>
              <Text type="danger" strong>
                İnaktif kayıt
              </Text>
              <Text>{row.obsoleteReason}</Text>
              <Text type="secondary">
                {row.obsoletedBy.fullName} - {moment(row.obsoletedAt).format('DD.MM.yyyy HH:mm:ss')}
              </Text>
            </Space>
          }
          style={{ marginBottom: '2rem' }}
        />
      )}
      <div>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Row>
            <Col span={8}>ID</Col>
            <Col span={16}>
              <span style={{ textDecoration: row?.isObsolete ? 'line-through' : 'inherit' }}>{row?.id}</span>
            </Col>
          </Row>
          <Row>
            <Col span={8}>Kayıt oluşturma</Col>
            <Col span={16}>
              {moment(row?.createdAt).format('DD.MM.yyyy HH:mm:ss')} ({row?.createdBy ? row.createdBy.fullName : 'Entegrasyon ile'})
            </Col>
          </Row>
          <Row>
            <Col span={8}>Son güncelleme</Col>
            <Col span={16}>{moment(row?.updatedAt).format('DD.MM.yyyy HH:mm:ss')}</Col>
          </Row>
        </Space>
      </div>
    </>
  );
}

function Actions(props: { grid?: Grid; row?: GridRow; onRowAdded: () => void; onRowSaved: () => void; onDelete: () => void }) {
  const [row] = useState(props.row);
  const [showRejectPopover, setShowRejectPopover] = useState(false);
  const [showRevisionPopover, setShowRevisionPopover] = useState(false);
  const [isCopyLoading, setCopyIsLoading] = useState(false);
  const [hasCopyPermission, setHasCopyPermission] = useState(false);
  const [hasBumpPermission, setHasBumpPermission] = useState(false);
  const [miniSdsSettings, setMiniSdsSettings] = useState<any>({
    ktFontSize: '8',
    edFontSize: '8',
    mdFontSize: '8',
  });

  const { user } = useContext(UserContext);

  useEffect(() => {
    setHasCopyPermission(hasCopyRecordPermission(user, props.grid?.id));
    setHasBumpPermission(
      (props.grid?.isRevision || false) && (props.row?.isLatestRevision || false) && hasBumpRevisionPermission(user, props.grid?.id),
    );
  }, [props.grid?.id, props.grid?.isRevision, props.row, user]);

  const onRejectVisibleChange = (visible: boolean) => {
    setShowRejectPopover(visible);
  };

  const onRevisionVisibleChange = (visible: boolean) => {
    setShowRevisionPopover(visible);
  };

  const onRevisionCancel = () => {
    setShowRevisionPopover(false);
  };

  const onRejectCancel = () => {
    setShowRejectPopover(false);
  };

  const exportPdf = () => {
    const isEn = String(props.grid?.name).endsWith('- EN');

    apiClient
      .post(`/records/record/export-pdf`, {
        templateName: !isEn ? 'SDS' : 'EnSDS',
        rowId: row?.id,
        gridId: row?.gridId,
        settings: {},
      })
      .then(async (response: any) => {
        const responseData = response.data;
        const buffer = responseData.file.data;
        const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', responseData.fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const exportMiniSds = () => {
    apiClient
      .post(`/records/record/export-pdf`, {
        templateName: 'MiniSDS',
        rowId: row?.id,
        gridId: row?.gridId,
        settings: miniSdsSettings,
      })
      .then(async (response: any) => {
        const responseData = response.data;
        const buffer = responseData.file.data;
        const blob = new Blob([new Uint8Array(buffer)], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', responseData.fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const confirmCopy = () => {
    const onOk = () => {
      setCopyIsLoading(true);
      apiClient
        .post(`records/record/copy`, {
          gridId: props.row?.gridId,
          rowId: props.row?.id,
        })
        .then(val => {
          setCopyIsLoading(false);
          props.onRowAdded();
          message.success('Kayıt başarıyla kopyalandı.');
        })
        .catch((reason: any) => {
          setCopyIsLoading(false);
          apiErrorMessage(reason);
        });
    };

    Modal.confirm({
      title: 'Kayıt kopyala',
      icon: <CopyOutlined />,
      content: 'Kaydı kopyalamak istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {String(props.grid?.name).includes('Safety Data Sheet') && (
        <>
          <div>
            <Button type="ghost" size="small" onClick={exportPdf}>
              SDS raporu oluştur
            </Button>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button type="ghost" size="small" onClick={exportMiniSds}>
              Mini SDS raporu oluştur
            </Button>
            {!String(props.grid?.name).endsWith('- EN') && (
              <MiniSDSSettingsForm
                settings={miniSdsSettings}
                onChange={settings => {
                  console.log(settings);
                  setMiniSdsSettings(settings);
                }}
              />
            )}
          </div>
        </>
      )}

      {hasCopyPermission && (
        <div>
          <Button type="ghost" size="small" onClick={confirmCopy} loading={isCopyLoading} disabled={row?.isObsolete || row?.isPassive}>
            Kaydı kopyala
          </Button>
        </div>
      )}

      {hasBumpPermission && (
        <div>
          <Popover
            trigger="click"
            visible={showRevisionPopover}
            onVisibleChange={onRevisionVisibleChange}
            destroyTooltipOnHide={true}
            content={<RevisionForm gridId={row?.gridId} rowId={row?.id} onRowAdded={props.onRowAdded} onCancel={onRevisionCancel} />}
          >
            <Button type="ghost" size="small" disabled={row?.isObsolete || row?.isPassive}>
              Revizyon atlat
            </Button>
          </Popover>
        </div>
      )}

      {!user?.roles?.some(r => r.name === 'Misafir') && (
        <div>
          <Popover
            trigger="click"
            visible={showRejectPopover}
            onVisibleChange={onRejectVisibleChange}
            destroyTooltipOnHide={true}
            content={<InactivateForm gridId={row?.gridId} rowId={row?.id} onRowSaved={props.onRowSaved} onCancel={onRejectCancel} />}
          >
            <Button type="ghost" size="small">
              Kaydı inaktif yap
            </Button>
          </Popover>
        </div>
      )}
    </div>
  );
}

function RevisionForm(props: { gridId?: string; rowId?: string; onRowAdded: () => void; onCancel: () => void }) {
  const [loading, setLoading] = useState(false);

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .post(`records/revision-bump`, {
        gridId: props.gridId,
        rowId: props.rowId,
        revisionComment: values.revisionComment,
      })
      .then(() => {
        props.onRowAdded();
        props.onCancel();
        message.success('Yeni revizyon başarıyla oluşturuldu.');
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish}>
      <Form.Item
        label="Açıklama"
        name="revisionComment"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 4000, message: 'En fazla 4000 karakter' },
        ]}
      >
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Gönder
          </Button>
          <Button type="default" htmlType="button" onClick={props.onCancel}>
            İptal
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

function InactivateForm(props: { gridId?: string; rowId?: string; onRowSaved: () => void; onCancel: () => void }) {
  const [loading, setLoading] = useState(false);

  const onFinish = (values: any) => {
    setLoading(true);
    apiClient
      .post(`records/rows/${props.rowId}/inactive?gridId=${props.gridId}`, values)
      .then(() => {
        props.onRowSaved();
        props.onCancel();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Form layout="vertical" name="basic" onFinish={onFinish}>
      <Form.Item
        label="Açıklama"
        name="obsoleteReason"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 4000, message: 'En fazla 4000 karakter' },
        ]}
      >
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={loading}>
            Gönder
          </Button>
          <Button type="default" htmlType="button" onClick={props.onCancel}>
            İptal
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}

function Integrations(props: { grid?: Grid; gridRow?: GridRow; integrations?: Integration[] }) {
  const [loading, setLoading] = useState(false);
  const [executionRecords, setExecutionRecords] = useState<ExportRecord[]>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  const tableColumns = [
    {
      title: 'Aktarım no',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Entegrasyon',
      dataIndex: ['integrationExecution', 'integration', 'name'],
      key: 'integrationName',
    },
    {
      title: 'Aktarım zamanı',
      dataIndex: ['integrationExecution', 'createdAt'],
      key: 'createdAt',
      render: (createdAt: Date) => moment(createdAt).format('DD.MM.yyyy HH:mm:ss'),
    },
    {
      title: 'Çalışma şekli',
      dataIndex: ['integrationExecution', 'isManuallyTriggered'],
      key: 'trigger',
      render: (isManuallyTriggered: boolean, er: ExportRecord) =>
        isManuallyTriggered
          ? 'Elle (' +
            er.integrationExecution.executedBy.fullName +
            (er.integrationExecution.isFileImport ? ', dosyadan aktarım' : '') +
            ')'
          : 'Otomatik',
    },
    {
      title: 'Sonuç',
      dataIndex: ['success'],
      key: 'success',
      render: (success: boolean) => (success ? 'Aktarıldı' : 'Aktarılamadı'),
    },
    {
      title: 'Geri bildirim',
      dataIndex: ['resultFeedback'],
      key: 'resultFeedback',
    },
  ];

  const loadExecutionRecords = useCallback(() => {
    setLoading(true);
    let offset = (tableCurrent - 1) * tablePageSize;
    const queryParams = [`gridId=${props.grid?.id}`, `recordId=${props.gridRow?.id}`, `limit=${tablePageSize}`, `offset=${offset}`];
    const queryParamsStr = queryParams.join('&');

    apiClient
      .get(`integrations/execution-records?${queryParamsStr}`)
      .then((response: any) => {
        setExecutionRecords(response.data.rows as any[]);
        setTableTotal(response.data.count);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.grid?.id, props.gridRow?.id, tableCurrent, tablePageSize]);

  useEffect(() => {
    loadExecutionRecords();
  }, [loadExecutionRecords]);

  const exportSelectedRows = (integrationId: string) => {
    const integration = props.integrations?.find(i => i.id === integrationId);
    if (!integration) {
      message.error('Entegrasyon bulunamadı.');
      return;
    }

    const body = {
      gridRowIds: [props.gridRow?.id],
    };

    setLoading(true);
    apiClient
      .post(`integrations/${integrationId}/execute`, body)
      .then((response: any) => {
        const execution = response.data as IntegrationExecution;
        if (execution.hasError) {
          message.error(execution.errorMessage);
        } else {
          if (execution.recordCount) {
            message.success(execution.recordCount + ' adet kayıt başarıyla aktarıldı');
            loadExecutionRecords();
          } else {
            message.info('Kayıt, son aktarımdan sonra güncellenmemiş. Herhangi bir kayıt aktarımı yapılmadı.');
          }
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => setLoading(false));
  };

  const onTableChange = (pagination: any) => {
    setTableCurrent(pagination.current);
    setTablePageSize(pagination.pageSize);
  };

  return (
    <>
      {(props.integrations?.length || 0) > 0 ? (
        <div style={{ marginBottom: '3rem' }}>
          <p>Kaydı dışa aktar</p>
          <Space>
            {props.integrations?.map(i => (
              <Button
                key={i.id}
                type="primary"
                style={{ width: '15rem', textAlign: 'left' }}
                onClick={() => exportSelectedRows(i.id)}
                disabled={props.gridRow?.recordStatus !== 'READY' || props.gridRow?.isObsolete || props.gridRow?.isPassive}
              >
                {i.name}
              </Button>
            ))}
          </Space>
        </div>
      ) : (
        <Empty description="Dışa aktarım mevcut değil" style={{ textAlign: 'left', marginLeft: '3rem', marginBottom: '3rem' }} />
      )}
      <p>Geçmiş dışa aktarımlar</p>
      <Table
        columns={tableColumns}
        dataSource={executionRecords}
        loading={loading}
        scroll={{ x: '100%' }}
        onChange={onTableChange}
        pagination={{
          current: tableCurrent,
          total: tableTotal,
          pageSize: tablePageSize,
          pageSizeOptions: ['10', '25', '50', '100'],
          position: ['bottomCenter'],
          showTotal: (total, range) => `Toplam ${total} kayıt. ${range[0]}-${range[1]} arası gösteriliyor.`,
          showSizeChanger: true,
        }}
      />
    </>
  );
}

function RecordComments(props: { gridId?: string; recordId?: string }) {
  const { TextArea } = Input;
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [comments, setComments] = useState<CommentProps[]>([]);

  const { user } = useContext(UserContext);
  const [nameInitials, setNameInitials] = useState<string>();
  const textareaRef = useRef<any>(null);

  useEffect(() => {
    if (!user) {
      setNameInitials(undefined);
    } else {
      const nameArray = user.fullName?.split(' ');
      if (!nameArray?.length) {
        setNameInitials('??');
      }
      const surname = nameArray[nameArray.length - 1];
      setNameInitials(nameArray[0].substr(0, 1) + surname.substr(0, 1));
    }
  }, [user]);

  interface CommentProps {
    id: string;
    user: User;
    avatar: any;
    content: React.ReactNode;
    createdAt: string;
    type: string;
  }

  const loadComments = useCallback(() => {
    if (!props.gridId || !props.recordId) {
      return;
    }
    apiClient
      .get(`comments?gridId=${props.gridId}&recordId=${props.recordId}`)
      .then((response: any) => {
        setComments(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.gridId, props.recordId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmit = (values: any) => {
    if (!values) {
      return;
    }

    apiClient
      .post(`comments`, {
        gridId: props.gridId,
        recordId: props.recordId,
        content: values.content,
        type: values.type,
      })
      .then(() => {
        setSubmitting(false);
        loadComments();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
    form.resetFields();
  };

  const options = [
    {
      label: 'Sorun',
      value: 'issue',
      icon: <BugOutlined />,
      tooltip: 'Bir sorun bildirin',
    },
    {
      label: 'Talep',
      value: 'request',
      icon: <FileAddOutlined />,
      tooltip: 'Bir talepte bulunun',
    },
    {
      label: 'Öneri',
      value: 'suggest',
      icon: <CommentOutlined />,
      tooltip: 'Bir öneride bulunun',
    },
  ];

  const { Option } = Select;

  const Editor = () => {
    return (
      <div>
        <Form layout="vertical" onFinish={handleSubmit} form={form}>
          <Form.Item name="content">
            <TextArea rows={4} ref={textareaRef} />
          </Form.Item>
          <Form.Item name="type" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <Select placeholder="Yorum tipi seçiniz">
              {options?.map(option => (
                <Option key={option.value} value={option.value}>
                  <Tooltip title={option.tooltip}>
                    <span>
                      {option.icon} {option.label}
                    </span>
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" loading={submitting} type="primary">
              Yorum ekle
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  const deleteConfirm = (id: string) => {
    const onOk = () => deleteComment(id);
    Modal.confirm({
      title: 'Yorumu sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Yorumunuzu silmek istediğinize emin misiniz ?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const deleteComment = (id: string) => {
    apiClient
      .delete(`comments/${id}`)
      .then(() => {
        loadComments();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const getTypeTag = (type: string) => {
    switch (type) {
      case 'issue':
        return (
          <Tag color="red" style={{ transform: 'rotate(270deg)', height: '100%', display: 'flex', alignItems: 'center' }}>
            Sorun
          </Tag>
        );
      case 'request':
        return (
          <Tag color="blue" style={{ transform: 'rotate(270deg)', height: '100%', display: 'flex', alignItems: 'center' }}>
            Talep
          </Tag>
        );
      case 'suggest':
        return (
          <Tag color="green" style={{ transform: 'rotate(270deg)', height: '100%', display: 'flex', alignItems: 'center' }}>
            Öneri
          </Tag>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {comments.length > 0 && (
        <List
          dataSource={comments}
          itemLayout="horizontal"
          renderItem={(props: CommentProps) => {
            let initials = '';
            const nameArray = props.user.fullName.split(' ');
            if (nameArray.length > 1) {
              const surname = nameArray[nameArray.length - 1];
              initials = nameArray[0].substr(0, 1) + surname.substr(0, 1);
            } else {
              initials = nameArray[0].substr(0, 1);
            }
            return (
              <List.Item>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: '10px',
                    height: '66px',
                  }}
                >
                  {/* Yorum kısmı */}
                  <div style={{ flexGrow: 1 }}>
                    <Comment
                      author={props.user.fullName}
                      avatar={<Avatar style={{ backgroundColor: '#87d068', marginRight: '0.25rem' }}>{initials}</Avatar>}
                      content={props.content}
                      datetime={moment(props.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      actions={[
                        <span
                          key="comment-nested-reply-to"
                          onClick={() => {
                            deleteConfirm(props.id);
                          }}
                        >
                          Sil
                        </span>,
                      ]}
                    />
                  </div>

                  {/* Yorum Tipi kısmı */}
                  <div style={{ marginLeft: '10px', whiteSpace: 'nowrap', height: '25px' }}>{getTypeTag(props.type)}</div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
      <Comment
        avatar={<Avatar style={{ backgroundColor: '#87d068', marginRight: '0.25rem' }}>{nameInitials}</Avatar>}
        content={<Editor />}
      />
    </div>
  );
}
